import { useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Field,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { CheckboxCard } from "@/components/CheckboxCard";
import { RowListSkeleton } from "@/components/RowListSkeleton";
import { FormActions, Toolbar } from "@jbnl/shared-ui";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { VolgordeKnoppen } from "@/components/VolgordeKnoppen";
import { verplaats } from "@/lib/volgorde";
import {
  LEEFTIJDSGROEP,
  leeftijdsgroepJaren,
  leeftijdsgroepLabel,
  leeftijdsgroepMetJaren,
  useCreateKlasse,
  useDeleteKlasse,
  useReorderKlassen,
  useStamKlassen,
  useUpdateKlasse,
  type KlasseStam,
} from "./useStamgegevens";

/**
 * The leeftijdsgroepen are a set rather than one value, because Brandbestrijding takes both - so
 * it implies nothing about a ploeg's age band, which the klassen rule skips rather than fails on.
 */
export function Klassen() {
  const { t } = useTranslation("stamgegevens");
  const { data: klassen, isPending } = useStamKlassen();
  const reorder = useReorderKlassen();

  function verzet(index: number, delta: number) {
    reorder.mutate(verplaats(klassen ?? [], index, delta).map((k) => k.id));
  }

  return (
    <>
      <Toolbar>
        <NieuweKlasse />
      </Toolbar>

      <Card>
        <CardContent className="pt-6">
          {isPending ? (
            <RowListSkeleton
              subtitle
              actions={["w-20", "w-24", "size-8", "size-8"]}
            />
          ) : (
            <ul className="divide-y">
              {klassen?.map((klasse, index) => (
                <li
                  key={klasse.id}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <span>
                    <span className="font-medium">{klasse.name}</span>
                    <span className="block text-xs text-muted-foreground">
                      {klasse.leeftijdsgroepen
                        .map(leeftijdsgroepMetJaren)
                        .join(t("leeftijdsgroep.scheiding"))}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1">
                    <WijzigKlasse klasse={klasse} />
                    {!klasse.inUse && <VerwijderKlasse klasse={klasse} />}
                    <VolgordeKnoppen
                      label={klasse.name}
                      eerste={index === 0}
                      laatste={index === (klassen?.length ?? 0) - 1}
                      disabled={reorder.isPending}
                      onOmhoog={() => verzet(index, -1)}
                      onOmlaag={() => verzet(index, 1)}
                    />
                  </span>
                </li>
              ))}
            </ul>
          )}
          {reorder.error && (
            <p className="mt-2 text-sm text-destructive">
              {reorder.error.message}
            </p>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function NieuweKlasse() {
  const { t } = useTranslation("stamgegevens");
  const create = useCreateKlasse();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus />
          {t("klassen.toevoegen")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("klassen.toevoegen")}</DialogTitle>
        </DialogHeader>
        <KlasseVelden
          key={String(open)}
          submitLabel={t("klassen.submitToevoegen")}
          pending={create.isPending}
          error={create.error}
          onSubmit={(waarden) =>
            create.mutate(waarden, { onSuccess: () => setOpen(false) })
          }
        />
      </DialogContent>
    </Dialog>
  );
}

function WijzigKlasse({ klasse }: { klasse: KlasseStam }) {
  const { t } = useTranslation(["stamgegevens", "common"]);
  const update = useUpdateKlasse();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          {t("klassen.wijzigen")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("klassen.wijzigTitle", { naam: klasse.name })}
          </DialogTitle>
        </DialogHeader>
        <KlasseVelden
          key={String(open)}
          klasse={klasse}
          submitLabel={t("common:actions.save")}
          pending={update.isPending}
          error={update.error}
          onSubmit={(waarden) =>
            update.mutate(
              { id: klasse.id, ...waarden },
              { onSuccess: () => setOpen(false) },
            )
          }
        />
      </DialogContent>
    </Dialog>
  );
}

function VerwijderKlasse({ klasse }: { klasse: KlasseStam }) {
  const { t } = useTranslation("stamgegevens");
  const remove = useDeleteKlasse();

  return (
    <ConfirmDelete
      trigger={
        <Button variant="ghost" size="sm" disabled={remove.isPending}>
          {t("klassen.verwijderen")}
        </Button>
      }
      title={t("klassen.verwijderVraag", { naam: klasse.name })}
      description={t("klassen.verwijderDescription")}
      confirmLabel={t("klassen.verwijderConfirm", { naam: klasse.name })}
      onConfirm={() => remove.mutate(klasse.id)}
    />
  );
}

function KlasseVelden({
  klasse,
  submitLabel,
  pending,
  error,
  onSubmit,
}: {
  klasse?: KlasseStam;
  submitLabel: string;
  pending: boolean;
  error: Error | null;
  onSubmit: (waarden: { name: string; leeftijdsgroepen: number[] }) => void;
}) {
  const { t } = useTranslation(["stamgegevens", "common"]);
  const [name, setName] = useState(klasse?.name ?? "");
  const [groepen, setGroepen] = useState<number[]>(
    klasse?.leeftijdsgroepen ?? [],
  );

  function wissel(groep: number) {
    setGroepen((vorige) =>
      vorige.includes(groep)
        ? vorige.filter((g) => g !== groep)
        : [...vorige, groep],
    );
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({ name, leeftijdsgroepen: groepen });
      }}
    >
      <Field>
        <FieldLabel htmlFor="klasse-naam">{t("klassen.naam")}</FieldLabel>
        <Input
          id="klasse-naam"
          value={name}
          maxLength={120}
          required
          onChange={(e) => setName(e.target.value)}
        />
      </Field>

      <FieldSet>
        <FieldLegend variant="label">{t("klassen.openVoor")}</FieldLegend>
        <ul className="grid gap-2 sm:grid-cols-2">
          {[LEEFTIJDSGROEP.junioren, LEEFTIJDSGROEP.aspiranten].map((groep) => (
            <li key={groep}>
              <CheckboxCard
                id={`groep-${klasse?.id ?? "nieuw"}-${groep}`}
                title={leeftijdsgroepLabel(groep)}
                description={leeftijdsgroepJaren(groep)}
                checked={groepen.includes(groep)}
                onCheckedChange={() => wissel(groep)}
              />
            </li>
          ))}
        </ul>
        <FieldDescription>{t("klassen.openVoorUitleg")}</FieldDescription>
      </FieldSet>

      <FormActions>
        {error && <p className="text-sm text-destructive">{error.message}</p>}
        <Button type="submit" disabled={pending || groepen.length === 0}>
          {pending ? t("common:actions.busy") : submitLabel}
        </Button>
      </FormActions>
    </form>
  );
}
